import { z } from "zod";
import { bandKey, MAX_BANDS, MAX_NAME_LENGTH, parseLineup } from "./concerts-parse";
import { CONCERT_PREFIX } from "./question-draw";

/**
 * Le collage des concerts (spec, section 3.7 ; ticket 6, #513), partage par le backend, qui relit
 * le texte et ecrit, et par le frontal, qui rejoue la reponse.
 *
 * Une seule route, `POST /api/t/concerts/coller`, en deux temps :
 *
 * - **apercu** (corps `{ text }`, sans `selected`) : la liste nettoyee par `parseLineup`, chaque
 *   groupe avec son compteur et s'il est deja sur la frise. Rien n'est ecrit ;
 * - **ajout** (corps `{ text, selected }`) : chaque groupe nouveau dont le nom est dans `selected`
 *   devient un point « a dater » de la piste musique, titre « Concert : {groupe} », prive, origine
 *   `concerts`, note « vu N fois » quand N > 1. Un nom de `selected` absent du texte est ignore.
 *
 * **Deja sur la frise** : un point de la timeline (toutes pistes) dont le titre est celui que le
 * collage poserait, casse ignoree (`bandKey`, la meme cle que le dedoublonnage du lecteur).
 * Recoller ne cree donc aucun doublon.
 */

/** Le texte colle le plus long, en caracteres (le corps est plafonne a 64 Ko par ailleurs). */
export const MAX_PASTE_CHARS = 50_000;

export const pasteConcertsBodySchema = z.strictObject({
  text: z
    .string()
    .max(MAX_PASTE_CHARS, `Texte collé : ${String(MAX_PASTE_CHARS)} caractères au plus.`),
  selected: z
    .array(z.string().max(MAX_NAME_LENGTH * 4))
    .max(MAX_BANDS, `${String(MAX_BANDS)} groupes au plus.`)
    .optional(),
});
export type PasteConcertsBody = z.infer<typeof pasteConcertsBodySchema>;

export const pastedBandSchema = z.object({
  key: z.string(),
  name: z.string(),
  /** Nombre de lignes du texte qui le citent. */
  count: z.number().int().positive(),
  /** Vrai quand la frise a deja un point « Concert : {groupe} ». */
  alreadyThere: z.boolean(),
  /** Vrai quand cet appel vient de creer son point. */
  added: z.boolean(),
});
export type PastedBand = z.infer<typeof pastedBandSchema>;

export const pasteConcertsResultSchema = z.object({
  /** Vrai pour un ajout (`selected` envoye), faux pour un apercu. */
  applied: z.boolean(),
  bands: z.array(pastedBandSchema),
  /** Vrai quand des groupes au-dela de `MAX_BANDS` ont ete ignores. */
  truncated: z.boolean(),
  recognized: z.number().int().nonnegative(),
  alreadyThere: z.number().int().nonnegative(),
  added: z.number().int().nonnegative(),
});
export type PasteConcertsResult = z.infer<typeof pasteConcertsResultSchema>;

/** Le titre du point d'un groupe. */
export const concertTitle = (name: string): string => `${CONCERT_PREFIX}${name}`;

/** La note du point d'un groupe : « vu N fois » au-dela d'une fois, sinon rien. */
export const concertNote = (count: number): string | null =>
  count > 1 ? `vu ${String(count)} fois` : null;

/**
 * Le plan d'un collage, sans rien ecrire : les groupes du texte, ceux deja sur la frise (d'apres
 * les titres de ses points), et ceux a ajouter (nouveaux et choisis ; aucun sans `selected`).
 */
export function pastePlan(
  text: string,
  existingTitles: readonly string[],
  selected?: readonly string[],
): PasteConcertsResult {
  const { bands, truncated } = parseLineup(text);
  const existing = new Set(existingTitles.map(bandKey));
  const chosen = new Set((selected ?? []).map(bandKey));
  const planned = bands.map((band) => {
    const alreadyThere = existing.has(bandKey(concertTitle(band.name)));
    return { ...band, alreadyThere, added: !alreadyThere && chosen.has(band.key) };
  });
  return {
    applied: selected !== undefined,
    bands: planned,
    truncated,
    recognized: planned.length,
    alreadyThere: planned.filter((band) => band.alreadyThere).length,
    added: planned.filter((band) => band.added).length,
  };
}
